import React, { useState, useEffect, useRef } from 'react';
import './BuildOrderView.css'; // Reuse the same CSS styles
import { authHeaders } from '../authUtils';
import { formatAbsoluteDate, formatRelativeDate } from '../formatRelativeDate';
import { buildEvidenceIndex } from '../evidenceLinks';
import ReplayPlayer from './ReplayPlayer';
import EvidenceMarkdown from './EvidenceMarkdown';
import BuildFingerprintPanel, { resolveFingerprints } from './BuildFingerprintPanel';

const PARSE_STAGE_LABELS = {
  queued: 'Queued…',
  downloading: 'Downloading replay file…',
  parsing: 'Parsing replay…',
  saving: 'Saving parsed data…',
};

const emptyAvailability = { sc2: new Set(), parsed: new Set(), ai: new Set() };

const ParsedReplayView = ({
  replay,
  assetAvailability = emptyAvailability,
  apiBaseUrl,
  isAuthenticated = false,
  onUnauthorized,
  onDeleted,
}) => {
  const [parsedReplay, setParsedReplay] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [parseLoading, setParseLoading] = useState(false);
  const [parseStage, setParseStage] = useState('');
  const [parseError, setParseError] = useState(null);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState(null);
  const [evidenceIndex, setEvidenceIndex] = useState(() => buildEvidenceIndex({}));
  const playheadSeekRef = useRef(null);

  useEffect(() => {
    setParseLoading(false);
    setParseStage('');
    setParseError(null);
    setEvidenceIndex(buildEvidenceIndex({}));
    fetchParsedReplay();
    fetchAIAnalysis();
    checkAnalysisStatus();
    checkParseStatus();
  }, [replay.replay_id]);

  // Evidence Links resolve against fact-tool rows for this replay (D28 / ticket 09).
  useEffect(() => {
    if (!apiBaseUrl || !replay?.replay_id || !parsedReplay) {
      return undefined;
    }
    let cancelled = false;
    const players = (parsedReplay.build_orders || [])
      .map((bo) => bo.player)
      .filter(Boolean);
    const base = `${apiBaseUrl}/replay_facts/${encodeURIComponent(replay.replay_id)}`;

    (async () => {
      try {
        const macroResults = await Promise.all(
          players.map(async (player) => {
            const response = await fetch(
              `${base}/macro_issues?player=${encodeURIComponent(player)}`
            );
            if (!response.ok) return { player, issues: [] };
            return response.json();
          })
        );
        const axes = await Promise.all(
          players.map(async (player) => {
            const response = await fetch(
              `${base}/axes?player=${encodeURIComponent(player)}`
            );
            if (!response.ok) return null;
            return response.json();
          })
        );
        const battlesRes = await fetch(`${base}/battles`);
        const battles = battlesRes.ok ? await battlesRes.json() : [];
        const lossesRes = await fetch(`${base}/losses_track?mode=battles`);
        const losses = lossesRes.ok ? await lossesRes.json() : { lanes: [] };
        const sightingsRes = await fetch(`${base}/sightings`);
        const sightingsPayload = sightingsRes.ok ? await sightingsRes.json() : {};
        if (cancelled) return;
        setEvidenceIndex(
          buildEvidenceIndex({
            macroResults: Array.isArray(macroResults) ? macroResults : [],
            axes: axes.filter(Boolean),
            battles: Array.isArray(battles) ? battles : [],
            losses,
            sightings: Array.isArray(sightingsPayload?.sightings)
              ? sightingsPayload.sightings
              : [],
          })
        );
      } catch {
        if (!cancelled) {
          setEvidenceIndex(buildEvidenceIndex({}));
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [apiBaseUrl, replay?.replay_id, parsedReplay]);

  // Periodically check analysis status - only when analysis is in progress
  useEffect(() => {
    if (!aiLoading) {
      // Don't start interval if analysis is not running
      return;
    }

    const interval = setInterval(() => {
      checkAnalysisStatus();
    }, 2000); // Check every 2 seconds

    return () => clearInterval(interval);
  }, [replay.replay_id, aiLoading]);

  // Poll parse status while parsing is in progress
  useEffect(() => {
    if (!parseLoading) {
      return;
    }

    const interval = setInterval(() => {
      checkParseStatus();
    }, 2000);

    return () => clearInterval(interval);
  }, [replay.replay_id, parseLoading]);

  const fetchAIAnalysis = async () => {
    console.log(`[AI Analysis] Fetching existing analysis for replay ${replay.replay_id}`);
    try {
      setAiError(null);
      const response = await fetch(`${apiBaseUrl}/ai_analysis/${replay.replay_id}`);
      console.log(`[AI Analysis] Fetch response status: ${response.status}`);
      
      if (!response.ok) {
        console.log('[AI Analysis] No existing analysis found');
        setAiAnalysis(null);
        return;
      }
      const data = await response.json();
      console.log('[AI Analysis] Existing analysis loaded:', data);
      setAiAnalysis(data);
    } catch (e) {
      console.error('[AI Analysis] Fetch failed:', e);
      setAiError(e.message);
    }
  };

  const jobIsRunning = (data) => Boolean(data.is_running ?? data.is_parsing ?? data.is_analyzing);
  const jobHasResult = (data) => Boolean(data.has_result ?? data.has_parsed);

  const checkAnalysisStatus = async () => {
    try {
      const response = await fetch(`${apiBaseUrl}/ai_analysis/status/${replay.replay_id}`);
      if (response.ok) {
        const data = await response.json();
        const running = jobIsRunning(data);
        console.log(`[AI Analysis] Status check: analyzing=${running}`);
        setAiLoading(running);

        // If analysis was running but now stopped, refresh the analysis (or show job error)
        if (aiLoading && !running) {
          console.log('[AI Analysis] Analysis completed, fetching result');
          if (data.error) {
            setAiError(data.error);
          }
          await fetchAIAnalysis();
        }
      }
    } catch (e) {
      console.error('[AI Analysis] Status check failed:', e);
    }
  };

  const triggerAIAnalysis = async (force = false) => {
    console.log(`[AI Analysis] Starting analysis for replay ${replay.replay_id}, force=${force}`);
    try {
      setAiError(null);
      if (!isAuthenticated) {
        return;
      }
      const url = `${apiBaseUrl}/ai_analysis/trigger/${replay.replay_id}${force ? '?force=true' : ''}`;
      console.log(`[AI Analysis] Sending POST request to: ${url}`);
      
      const response = await fetch(url, { method: 'POST', headers: authHeaders() });
      console.log(`[AI Analysis] Trigger response status: ${response.status}`);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error(`[AI Analysis] Trigger failed: ${response.status} - ${errorText}`);
        if (response.status === 401) {
          onUnauthorized?.();
          throw new Error('Authentication failed — returned to Guest');
        }
        throw new Error('Failed to trigger AI analysis');
      }
      
      const triggerResult = await response.json();
      console.log('[AI Analysis] Trigger successful:', triggerResult);
      
      // Set loading state and let the periodic status check handle the rest
      setAiLoading(true);
      console.log('[AI Analysis] Analysis triggered, status will be checked automatically');
      
    } catch (e) {
      console.error('[AI Analysis] Trigger failed:', e);
      setAiError(e.message);
      setAiLoading(false);
    }
  };

  const fetchParsedReplay = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`${apiBaseUrl}/parsed_replay/${replay.replay_id}`);
      
      if (!response.ok) {
        if (response.status === 404) {
          setParsedReplay(null);
          setError(
            isAuthenticated
              ? 'Parsed replay not found. You can trigger parsing.'
              : 'Parsed replay not found.'
          );
          return;
        }
        throw new Error('Failed to fetch parsed replay');
      }
      
      const data = await response.json();
      setParsedReplay(data);
      setError(null);
      setParseError(null);
      
      // Auto-select first player if available
      if (data.build_orders && data.build_orders.length > 0) {
        setSelectedPlayer(data.build_orders[0].player);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const checkParseStatus = async () => {
    try {
      const response = await fetch(`${apiBaseUrl}/parsed_replay/status/${replay.replay_id}`);
      if (!response.ok) {
        return;
      }
      const data = await response.json();
      const wasParsing = parseLoading;
      const running = jobIsRunning(data);
      const hasResult = jobHasResult(data);
      setParseLoading(running);
      setParseStage(data.stage || '');

      if (running) {
        setParseError(null);
        return;
      }

      if (data.error) {
        setParseError(data.error);
      }

      // Parsing just finished — refresh result or show failure
      if (wasParsing && !running) {
        if (hasResult) {
          await fetchParsedReplay();
        } else if (data.error) {
          setError(null);
        }
      } else if (!wasParsing && hasResult && !parsedReplay) {
        await fetchParsedReplay();
      }
    } catch (e) {
      console.error('[Parse] Status check failed:', e);
    }
  };

  const triggerParsing = async (force = false) => {
    try {
      setError(null);
      setParseError(null);
      if (!isAuthenticated) {
        return;
      }
      
      const url = `${apiBaseUrl}/parsed_replay/trigger/${replay.replay_id}${force ? '?force=true' : ''}`;
      const response = await fetch(url, {
        method: 'POST',
        headers: authHeaders()
      });
      
      if (!response.ok) {
        if (response.status === 401) {
          onUnauthorized?.();
          throw new Error('Authentication failed — returned to Guest');
        }
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to trigger parsing');
      }
      
      const result = await response.json();
      if (jobHasResult(result) && !jobIsRunning(result)) {
        await fetchParsedReplay();
        return;
      }
      if (!jobIsRunning(result)) {
        setParseError(
          force
            ? 'Parse was not started. Try again or check server logs.'
            : 'Parse was not started. This replay may be older than the allowed age — use Force.'
        );
        return;
      }
      setParsedReplay(null);
      setParseLoading(true);
      setParseStage(result.stage || 'queued');
    } catch (err) {
      setParseError(`Failed to trigger parsing: ${err.message}`);
      setParseLoading(false);
    }
  };

  const triggerForceReparse = async () => {
    console.log(`[Force Re-parse] Starting force re-parse for replay ${replay.replay_id}`);
    try {
      setError(null);
      setParseError(null);
      if (!isAuthenticated) {
        return;
      }
      
      const url = `${apiBaseUrl}/parsed_replay/force_reparse/${replay.replay_id}`;
      console.log(`[Force Re-parse] Sending POST request to: ${url}`);
      
      const response = await fetch(url, {
        method: 'POST',
        headers: authHeaders()
      });
      
      console.log(`[Force Re-parse] Response status: ${response.status}`);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error(`[Force Re-parse] Failed: ${response.status} - ${errorText}`);
        if (response.status === 401) {
          onUnauthorized?.();
          throw new Error('Authentication failed — returned to Guest');
        }
        throw new Error(errorText || 'Failed to trigger force re-parse');
      }
      
      const result = await response.json();
      console.log('[Force Re-parse] Success:', result);
      
      setParsedReplay(null);
      setParseLoading(true);
      setParseStage('queued');
      
    } catch (err) {
      console.error('[Force Re-parse] Error:', err);
      setParseError(`Failed to trigger force re-parse: ${err.message}`);
      setParseLoading(false);
    }
  };

  const formatTime = (seconds) => {
    if (typeof seconds === 'string') {
      // Handle time string format
      const date = new Date(seconds);
      return date.toLocaleTimeString();
    }
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatBytes = (n) => {
    if (n == null || Number.isNaN(Number(n))) return '—';
    const bytes = Number(n);
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };


  const hasAiAnalysis = Boolean(aiAnalysis) || assetAvailability.ai.has(replay.replay_id);
  const fingerprints = resolveFingerprints(replay, parsedReplay);
  const hasParsedAsset = assetAvailability.parsed.has(replay.replay_id) || Boolean(parsedReplay);

  return (
    <div className="build-order-view">
      <div className="replay-header">
        <h2>{replay.map_name}</h2>
        <div className="replay-meta">
          <span>Replay #{replay.replay_id}</span>
          <span>Duration: {formatTime(replay.game_length)}</span>
          <span title={formatAbsoluteDate(replay.replay_date)}>
            Date: {formatRelativeDate(replay.replay_date)}
          </span>
          <span>Winner: {replay.winning_player}</span>
        </div>
        <BuildFingerprintPanel
          fingerprints={fingerprints}
          showEmptyHint={hasParsedAsset && fingerprints.length === 0}
        />
        <div className="asset-badges" aria-label="Stored assets" style={{ marginTop: '10px' }}>
          <span
            className={`asset-badge ${assetAvailability.sc2.has(replay.replay_id) ? 'present' : 'missing'} sc2`}
            title={assetAvailability.sc2.has(replay.replay_id) ? 'SC2Replay file stored' : 'SC2Replay file missing'}
          >
            SC2
          </span>
          <span
            className={`asset-badge ${assetAvailability.parsed.has(replay.replay_id) || parsedReplay ? 'present' : 'missing'} parsed`}
            title={(assetAvailability.parsed.has(replay.replay_id) || parsedReplay) ? 'Parsed game available' : 'Not parsed yet'}
            data-testid="parsed-badge"
          >
            Parsed
          </span>
          <span
            className={`asset-badge ${assetAvailability.ai.has(replay.replay_id) || aiAnalysis ? 'present' : 'missing'} ai`}
            title={(assetAvailability.ai.has(replay.replay_id) || aiAnalysis) ? 'AI analysis available' : 'No AI analysis'}
            data-testid="ai-badge"
          >
            AI
          </span>
          {isAuthenticated && assetAvailability.sc2.has(replay.replay_id) && (
            <button
              type="button"
              className="refresh-btn"
              data-testid="sc2-download-btn"
              style={{ marginLeft: '8px' }}
              onClick={async () => {
                try {
                  const response = await fetch(
                    `${apiBaseUrl}/replays/${encodeURIComponent(replay.replay_id)}/sc2`,
                    { headers: authHeaders() }
                  );
                  if (response.status === 401) {
                    onUnauthorized?.();
                    return;
                  }
                  if (!response.ok) {
                    throw new Error('Download failed');
                  }
                  const blob = await response.blob();
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `${replay.replay_id}.SC2Replay`;
                  a.click();
                  URL.revokeObjectURL(url);
                } catch (err) {
                  setError(err.message);
                }
              }}
            >
              Download SC2
            </button>
          )}
          {isAuthenticated && (
            <button
              type="button"
              className="retry-btn"
              data-testid="replay-delete-btn"
              style={{ marginLeft: '8px' }}
              onClick={async () => {
                const ok = window.confirm(
                  `Delete replay ${replay.replay_id}? This removes the catalog row and all stored assets.`
                );
                if (!ok) return;
                try {
                  const response = await fetch(
                    `${apiBaseUrl}/replays/${encodeURIComponent(replay.replay_id)}`,
                    { method: 'DELETE', headers: authHeaders() }
                  );
                  if (response.status === 401) {
                    onUnauthorized?.();
                    return;
                  }
                  if (!response.ok) {
                    const text = await response.text();
                    throw new Error(text || 'Delete failed');
                  }
                  onDeleted?.();
                } catch (err) {
                  setError(err.message);
                }
              }}
            >
              Delete
            </button>
          )}
          {isAuthenticated && (
            <button
              type="button"
              className="retry-btn"
              data-testid="force-reparse-btn"
              onClick={triggerForceReparse}
              disabled={parseLoading}
              style={{
                marginLeft: '8px',
                backgroundColor: '#ff6b35',
                borderColor: '#ff6b35',
                opacity: parseLoading ? 0.6 : 1,
                cursor: parseLoading ? 'not-allowed' : 'pointer'
              }}
            >
              {parseLoading ? 'Parsing…' : 'Force Re-parse'}
            </button>
          )}
        </div>
      </div>

      {loading && !parseLoading && <div className="loading">Loading parsed replay...</div>}

      {parseLoading && (
        <div className="analysis-status parse-status">
          <div className="spinner"></div>
          <div>
            <strong>Parsing in progress…</strong>
            <div className="parse-status-detail">
              {PARSE_STAGE_LABELS[parseStage] || 'Working on this replay…'}
            </div>
          </div>
        </div>
      )}

      {parseError && !parseLoading && (
        <div className="error">
          <p>Parsing failed: {parseError}</p>
          <div>
            {isAuthenticated && (
              <>
                <button onClick={() => triggerParsing(true)} className="retry-btn" disabled={parseLoading}>
                  Retry Parsing (Force)
                </button>
              </>
            )}
          </div>
        </div>
      )}
      
      {error && !parseLoading && !parsedReplay && (
        <div className="error">
          <p>{error.startsWith('Parsed replay not found') ? error : `Error: ${error}`}</p>
          <div>
            <button onClick={fetchParsedReplay} className="retry-btn" disabled={parseLoading}>
              Retry
            </button>
            {isAuthenticated && (
              <>
                <button
                  onClick={() => triggerParsing(false)}
                  className="retry-btn"
                  style={{marginLeft: '10px'}}
                  disabled={parseLoading}
                >
                  Trigger Parsing (Today Only)
                </button>
                <button
                  onClick={() => triggerParsing(true)}
                  className="retry-btn"
                  style={{marginLeft: '10px'}}
                  disabled={parseLoading}
                >
                  Trigger Parsing (Force)
                </button>
              </>
            )}
          </div>
          {isAuthenticated && (
            <div style={{marginTop: '10px', fontSize: '0.9em', color: '#666'}}>
              <p><strong>Today Only:</strong> Only parses replays within the configured max age</p>
              <p><strong>Force:</strong> Parses any replay regardless of date</p>
            </div>
          )}
        </div>
      )}

      {parsedReplay && (
        <>
          {/* Game Info Section */}
          {parsedReplay.info && (
            <div className="game-info">
              <h3>Game Information</h3>
              <div className="info-grid">
                <div>Version: {parsedReplay.info.version}</div>
                <div>Game Type: {parsedReplay.info.game_type}</div>
                <div>Map: {parsedReplay.info.map_name}</div>
                <div data-testid="parsed-players">Players: {parsedReplay.info.players?.map(p => p.name).join(', ')}</div>
                {parsedReplay.info.duration_stats && (
                  <div>
                    Duration: {parsedReplay.info.duration_stats.game_length_mins}:
                    {parsedReplay.info.duration_stats.game_length_seconds?.toString().padStart(2, '0')}
                  </div>
                )}
                <div data-testid="artifact-size-sc2">
                  Replay file: {formatBytes(parsedReplay.artifact_sizes?.sc2_bytes)}
                </div>
                <div data-testid="artifact-size-parsed">
                  Parsed data: {formatBytes(parsedReplay.artifact_sizes?.parsed_bytes)}
                </div>
                <div data-testid="artifact-size-ai">
                  AI analysis: {formatBytes(parsedReplay.artifact_sizes?.ai_bytes)}
                </div>
              </div>
            </div>
          )}

          {/* Players Details */}
          {parsedReplay.info && parsedReplay.info.players && (
            <div className="players-info">
              <h3>Players</h3>
              <div className="players-grid">
                {parsedReplay.info.players.map((player, index) => (
                  <div key={index} className="player-card">
                    <div className="player-name">{player.name}</div>
                    <div className="player-details">
                      <span className="player-race">{player.race}</span>
                      <span className="player-team">Team {player.team_id}</span>
                      <span className={`player-result ${player.result.toLowerCase()}`}>
                        {player.result}
                      </span>
                      <span className="player-apm">APM: {player.apm?.toFixed(1)}</span>
                    </div>
                  </div>
                ))}
              </div>
              {parsedReplay.info.winner && (
                <div className="winner-info">
                  <strong>Winner: {parsedReplay.info.winner}</strong>
                  {parsedReplay.info.winning_team && (
                    <span> (Team {parsedReplay.info.winning_team})</span>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="build-order-section rp-breakout">
            <ReplayPlayer
              minimap={parsedReplay.minimap}
              buildOrders={parsedReplay.build_orders || []}
              selectedPlayer={selectedPlayer}
              onSelectedPlayerChange={setSelectedPlayer}
              onForceReparse={triggerForceReparse}
              parseLoading={parseLoading}
              isAuthenticated={isAuthenticated}
              playerTimelines={parsedReplay.player_timelines || null}
              cameras={parsedReplay.cameras || null}
              replayId={replay?.replay_id || null}
              apiBaseUrl={apiBaseUrl}
              schemaVersion={parsedReplay.schema_version || 0}
              mapFileHash={parsedReplay.info?.map_filehash || null}
              playheadSeekRef={playheadSeekRef}
            />
          </div>

          {/* AI Analysis Section */}
          <div className="build-order-section">
            <h3>AI Analysis</h3>
            
            {/* Analysis Status Indicator */}
            {aiLoading && (
              <div className="analysis-status" style={{
                backgroundColor: '#e3f2fd',
                border: '1px solid #2196f3',
                borderRadius: '4px',
                padding: '12px',
                marginBottom: '15px',
                display: 'flex',
                alignItems: 'center',
                gap: '10px'
              }}>
                <div className="spinner" style={{
                  width: '20px',
                  height: '20px',
                  border: '2px solid #f3f3f3',
                  borderTop: '2px solid #2196f3',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }}></div>
                <div>
                  <strong>🤖 AI Analysis in Progress...</strong>
                  <div style={{ fontSize: '0.9em', color: '#666', marginTop: '4px' }}>
                    Analyzing replay data and generating insights. This may take a few moments.
                  </div>
                </div>
              </div>
            )}
            
            {isAuthenticated && (
              <div style={{ marginBottom: '10px', display: 'flex', gap: '10px' }}>
                <button
                  className="retry-btn"
                  data-testid="ai-analysis-trigger"
                  onClick={() => triggerAIAnalysis(hasAiAnalysis)}
                  disabled={aiLoading}
                  style={{
                    opacity: aiLoading ? 0.6 : 1,
                    cursor: aiLoading ? 'not-allowed' : 'pointer'
                  }}
                >
                  {aiLoading
                    ? 'Running AI Analysis…'
                    : hasAiAnalysis
                      ? 'Force AI Analysis'
                      : 'Start AI Analysis'}
                </button>
              </div>
            )}
            {aiError && <div className="error">{aiError}</div>}
            {aiAnalysis ? (
              <div
                className="build-order-content ai-analysis-content"
                data-testid="ai-analysis-content"
              >
                <EvidenceMarkdown
                  markdown={aiAnalysis.markdown}
                  evidenceIndex={evidenceIndex}
                  onSeek={(t) => playheadSeekRef.current?.(t)}
                />
              </div>
            ) : !aiLoading && (
              <div className="no-data">No AI analysis yet</div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default ParsedReplayView;
